import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { formatCurrencyByCountry } from '../lib/constants'
import { openExternal } from '../lib/openExternal'
import Header from '../components/Header'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://hireit-production-ac0f.up.railway.app'

export default function Payouts() {
const { user, profile } = useAuth()
const navigate = useNavigate()
const [earnings, setEarnings] = useState({ total: 0, pending: 0, paid: 0 })
const [bookings, setBookings] = useState([])
const [loading, setLoading] = useState(true)
const [connecting, setConnecting] = useState(false)
const [openingDashboard, setOpeningDashboard] = useState(false)
const [stripeStatus, setStripeStatus] = useState(null)
const [statusLoading, setStatusLoading] = useState(true)
const [selectedItem, setSelectedItem] = useState(null)

useEffect(() => {
if (!user) return // ProtectedRoute sends logged-out visitors to sign-in
fetchEarnings()
checkStripeStatus()
}, [user])

const fetchEarnings = async () => {
setLoading(true)
let { data } = await supabase
.from('bookings')
.select('*, listings(id, title, images, country)')
.eq('owner_id', user.id)
.in('status', ['confirmed', 'completed'])
.order('created_at', { ascending: false })
const _hids = [...new Set((data || []).map(x => x.hirer_id).filter(Boolean))]
let _hmap = {}
if (_hids.length) {
const { data: _hp } = await supabase.from('public_profiles').select('id, full_name').in('id', _hids)
;(_hp || []).forEach(x => { _hmap[x.id] = x })
}
data = (data || []).map(x => ({ ...x, hirer: _hmap[x.hirer_id] || null }))

const b = data || []
const total = b.reduce((sum, booking) => sum + (booking.owner_payout || 0), 0)
const pending = b.filter(b => b.status === 'confirmed').reduce((sum, b) => sum + (b.owner_payout || 0), 0)
const paid = b.filter(b => b.status === 'completed').reduce((sum, b) => sum + (b.owner_payout || 0), 0)

setEarnings({ total, pending, paid })
setBookings(b)
setLoading(false)
}

const checkStripeStatus = async () => {
setStatusLoading(true)
if (!profile?.stripe_account_id) { setStatusLoading(false); return }
try {
const res = await fetch(`${BACKEND_URL}/stripe/connect/status/${profile.stripe_account_id}`)
const data = await res.json()
setStripeStatus(data)
} catch (e) {
console.log('Stripe status check failed:', e)
}
setStatusLoading(false)
}

const connectStripe = async () => {
setConnecting(true)
try {
const { data: emailData } = await supabase.rpc('get_user_email', { user_id: user.id })
const { data: { session } } = await supabase.auth.getSession()
const res = await fetch(`${BACKEND_URL}/stripe/connect/onboard`, {
method: 'POST',
headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
body: JSON.stringify({ userId: user.id, email: emailData })
})
const data = await res.json()
if (data.url) {
await supabase.from('profiles').update({ stripe_account_id: data.accountId }).eq('id', user.id)
await openExternal(data.url)
}
} catch (e) {
console.error('Stripe connect error:', e)
alert('Could not open Stripe. Please try again.')
}
setConnecting(false)
}

const openStripeDashboard = async () => {
setOpeningDashboard(true)
try {
const { data: { session } } = await supabase.auth.getSession()
const res = await fetch(`${BACKEND_URL}/stripe/connect/dashboard`, {
method: 'POST',
headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
body: JSON.stringify({ accountId: profile.stripe_account_id })
})
const data = await res.json()
if (data.url) await openExternal(data.url)
} catch (e) {
console.error('Dashboard error:', e)
alert('Could not open Stripe dashboard. Please try again.')
}
setOpeningDashboard(false)
}

// Group bookings by listing
const bookingsByItem = bookings.reduce((acc, b) => {
const listingId = b.listings?.id
if (!listingId) return acc
if (!acc[listingId]) {
acc[listingId] = {
listing: b.listings,
bookings: [],
totalEarnings: 0,
completedCount: 0,
pendingCount: 0,
totalHours: 0,
hirers: new Set(),
}
}
acc[listingId].bookings.push(b)
acc[listingId].totalEarnings += b.owner_payout || 0
acc[listingId].totalHours += b.hours || 0
if (b.status === 'completed') acc[listingId].completedCount++
if (b.status === 'confirmed') acc[listingId].pendingCount++
if (b.hirer?.full_name) acc[listingId].hirers.add(b.hirer.full_name)
return acc
}, {})

const selectedItemData = selectedItem ? bookingsByItem[selectedItem] : null

if (loading) return <div><Header /><div className="spinner-wrap"><div className="spinner" /></div></div>

const isStripeConnected = stripeStatus?.onboarded || false
const hasStripeAccount = !!profile?.stripe_account_id

return (
<div>
<Header />
<div className="container" style={{ maxWidth: 700, padding: '1.5rem 1rem' }}>
<div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1.5rem' }}>
<button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>← Back</button>
<h1 style={{ fontSize: 22, fontWeight: 600, color: 'var(--navy)' }}>Payouts</h1>
</div>

{/* Stripe Connection */}
<div className="card card-body" style={{ marginBottom: '1.5rem' }}>
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
<div style={{ flex: 1, minWidth: 240 }}>
<div style={{ fontWeight: 700, marginBottom: 4, color: 'var(--navy)' }}>Bank account</div>
<div style={{ fontSize: 14, color: 'var(--text-muted)' }}>
{statusLoading
? 'Checking your bank account status...'
: isStripeConnected
? '✅ Your bank account is connected. Earnings will be paid out automatically.'
: hasStripeAccount
? '⚠️ Bank setup started but not yet completed. Click below to finish.'
: 'Connect your bank account to receive payouts from hirers.'}
</div>
<div style={{ fontSize: 12, color: 'var(--text-faint)', marginTop: 8 }}>
💡 Opens Stripe in a new tab — HireIt stays open here
</div>
</div>
{statusLoading ? (
<div className="spinner" style={{ width: 22, height: 22 }} />
) : isStripeConnected ? (
<button className="btn btn-ghost" onClick={openStripeDashboard} disabled={openingDashboard}>
{openingDashboard ? (
<>
<span className="spinner" style={{ width: 14, height: 14, borderWidth: 2, display: 'inline-block', marginRight: 6 }} />
Opening Stripe...
</>
) : (
'View Stripe dashboard ↗'
)}
</button>
) : (
<button className="btn btn-gradient" onClick={connectStripe} disabled={connecting}>
{connecting ? (
<>
<span className="spinner" style={{ width: 14, height: 14, borderWidth: 2, display: 'inline-block', marginRight: 6, borderTopColor: '#fff' }} />
Opening Stripe...
</>
) : (
hasStripeAccount ? '💳 Finish setup ↗' : '💳 Connect bank account ↗'
)}
</button>
)}
</div>
</div>

{/* Earnings summary */}
<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: '1.5rem' }}>
{/* TODO: multi-currency aggregates. earnings.total/pending/paid sum owner_payout across ALL the owner's bookings,
    which may span multiple listing currencies. Summing AUD + GBP raw numbers is meaningless.
    Keeping 'Australia' here preserves current display; real fix needs group-by-currency or base-currency conversion. */}
{[
{ label: 'Total earnings', val: formatCurrencyByCountry(earnings.total, 'Australia'), color: 'var(--green)' },
{ label: 'Pending payout', val: formatCurrencyByCountry(earnings.pending, 'Australia'), color: 'var(--amber)' },
{ label: 'Paid out', val: formatCurrencyByCountry(earnings.paid, 'Australia'), color: 'var(--blue)' },
].map(s => (
<div key={s.label} style={{ background: 'var(--gray-100)', borderRadius: 12, padding: '1rem' }}>
<div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
<div style={{ fontSize: 22, fontWeight: 600, color: s.color }}>{s.val}</div>
</div>
))}
</div>

{/* Earnings history */}
<h3 style={{ fontWeight: 700, marginBottom: 12, color: 'var(--navy)' }}>Earnings history</h3>
<p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
💡 Tap any item name to see its full booking history
</p>
{bookings.length === 0 ? (
<div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
<div style={{ fontSize: 48, marginBottom: 12 }}>💰</div>
<p>No earnings yet. List items to start earning!</p>
<button className="btn btn-gradient" style={{ marginTop: 16 }} onClick={() => navigate('/list-item')}>
List an item
</button>
</div>
) : (
<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
{bookings.map(b => (
<div key={b.id} className="card card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
<div style={{ flex: 1, minWidth: 0 }}>
<div
onClick={() => b.listings?.id && setSelectedItem(b.listings.id)}
style={{
fontWeight: 600,
color: 'var(--primary)',
cursor: 'pointer',
display: 'inline-flex',
alignItems: 'center',
gap: 4,
textDecoration: 'underline',
}}
>
{b.listings?.title}
<span style={{ fontSize: 11 }}>→</span>
</div>
<div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
{new Date(b.created_at).toLocaleDateString('en-AU')} · {b.hours} hrs
{b.hirer?.full_name && <> · Hired by {b.hirer.full_name}</>}
</div>
</div>
<div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
<div>
<div style={{ fontWeight: 600, color: 'var(--green)', fontSize: 16 }}>
{formatCurrencyByCountry(b.owner_payout || 0, b.listings?.country || 'Australia')}
</div>
<div style={{ fontSize: 11, color: 'var(--text-muted)' }}>after 12% fee</div>
</div>
<span className={`badge badge-${b.status === 'completed' ? 'green' : 'amber'}`}>
{b.status === 'completed' ? 'Paid' : 'Pending'}
</span>
</div>
</div>
))}
</div>
)}
</div>

{/* Item detail modal */}
{selectedItemData && (
<div
className="modal-overlay"
onClick={() => setSelectedItem(null)}
style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
>
<div
className="modal-box"
onClick={e => e.stopPropagation()}
style={{ maxWidth: 600, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}
>
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 8 }}>
<h3 style={{ fontWeight: 700, color: 'var(--navy)', flex: 1 }}>
{selectedItemData.listing.title}
</h3>
<button
onClick={() => setSelectedItem(null)}
style={{ background: 'transparent', border: 'none', fontSize: 24, cursor: 'pointer', color: 'var(--text-muted)', padding: 0, lineHeight: 1 }}
>
×
</button>
</div>

{selectedItemData.listing.images?.[0] && (
<img
src={selectedItemData.listing.images[0]}
alt={selectedItemData.listing.title}
style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 10, marginBottom: 12 }}
/>
)}

{/* Item stats */}
<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8, marginBottom: 16 }}>
<div style={{ background: 'var(--primary-pale)', borderRadius: 10, padding: '10px 12px' }}>
<div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total earned</div>
<div style={{ fontSize: 18, fontWeight: 700, color: 'var(--green)' }}>
{formatCurrencyByCountry(selectedItemData.totalEarnings, selectedItemData.listing.country || 'Australia')}
</div>
</div>
<div style={{ background: 'var(--gray-100)', borderRadius: 10, padding: '10px 12px' }}>
<div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Bookings</div>
<div style={{ fontSize: 18, fontWeight: 700, color: 'var(--navy)' }}>
{selectedItemData.bookings.length}
</div>
<div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
{selectedItemData.completedCount} done · {selectedItemData.pendingCount} active
</div>
</div>
<div style={{ background: 'var(--gray-100)', borderRadius: 10, padding: '10px 12px' }}>
<div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total hours</div>
<div style={{ fontSize: 18, fontWeight: 700, color: 'var(--navy)' }}>
{selectedItemData.totalHours}
</div>
</div>
<div style={{ background: 'var(--gray-100)', borderRadius: 10, padding: '10px 12px' }}>
<div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Unique hirers</div>
<div style={{ fontSize: 18, fontWeight: 700, color: 'var(--navy)' }}>
{selectedItemData.hirers.size}
</div>
</div>
</div>

{/* Booking list */}
<div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)', marginBottom: 8 }}>
All bookings ({selectedItemData.bookings.length})
</div>
<div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
{selectedItemData.bookings.map(b => (
<div
key={b.id}
style={{ background: 'var(--gray-50)', borderRadius: 8, padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
>
<div style={{ flex: 1, minWidth: 0 }}>
<div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)' }}>
{b.hirer?.full_name || 'Unknown hirer'}
</div>
<div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
{new Date(b.created_at).toLocaleDateString('en-AU')} · {b.hours} hrs
</div>
</div>
<div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
<div style={{ fontWeight: 700, color: 'var(--green)', fontSize: 14 }}>
{formatCurrencyByCountry(b.owner_payout || 0, b.listings?.country || 'Australia')}
</div>
<span className={`badge badge-${b.status === 'completed' ? 'green' : 'amber'}`} style={{ fontSize: 10 }}>
{b.status === 'completed' ? 'Paid' : 'Pending'}
</span>
</div>
</div>
))}
</div>

<div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
<button
className="btn btn-ghost"
onClick={() => { setSelectedItem(null); navigate(`/listing/${selectedItemData.listing.id}`) }}
style={{ flex: 1, minWidth: 140 }}
>
📦 View listing
</button>
<button
className="btn btn-gradient"
onClick={() => setSelectedItem(null)}
style={{ flex: 1, minWidth: 140 }}
>
Close
</button>
</div>
</div>
</div>
)}
</div>
)
}